import { Injectable, signal, computed } from '@angular/core';
import { PublicProduct } from '../../models/product.model';

export interface CartItem {
    product: PublicProduct;
    quantity: number;
}

const CART_KEY = 'sqw_cart';

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private _items = signal<CartItem[]>(this.loadCart());

    // Public signals
    readonly items = this._items.asReadonly();
    readonly itemCount = computed(() =>
        this._items().reduce((sum, item) => sum + item.quantity, 0)
    );
    readonly total = computed(() =>
        this._items().reduce((sum, item) => sum + (item.product.precio * item.quantity), 0)
    );
    readonly isEmpty = computed(() => this._items().length === 0);

    private loadCart(): CartItem[] {
        try {
            const stored = localStorage.getItem(CART_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    }

    private saveCart(): void {
        localStorage.setItem(CART_KEY, JSON.stringify(this._items()));
    }

    addItem(product: PublicProduct, quantity: number = 1): void {
        const items = this._items();
        const existingIndex = items.findIndex(item => item.product.id === product.id);

        if (existingIndex >= 0) {
            const updated = [...items];
            updated[existingIndex].quantity += quantity;
            this._items.set(updated);
        } else {
            this._items.set([...items, { product, quantity }]);
        }
        this.saveCart();
    }

    removeItem(productId: number): void {
        this._items.set(this._items().filter(item => item.product.id !== productId));
        this.saveCart();
    }

    updateQuantity(productId: number, quantity: number): void {
        if (quantity <= 0) {
            this.removeItem(productId);
            return;
        }

        const items = this._items();
        const index = items.findIndex(item => item.product.id === productId);

        if (index >= 0) {
            const updated = [...items];
            updated[index].quantity = quantity;
            this._items.set(updated);
            this.saveCart();
        }
    }

    clearCart(): void {
        this._items.set([]);
        localStorage.removeItem(CART_KEY);
    }

    getItem(productId: number): CartItem | undefined {
        return this._items().find(item => item.product.id === productId);
    }
}
